"use client";

import { useState } from "react";
import Link from "next/link";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LEGAL, type PlanId, PLANS } from "@/lib/legal";

type Mode = "pay" | "reserve";

type CheckoutDialogProps = {
  planId: PlanId | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type FormState = {
  name: string;
  email: string;
  company: string;
  taxId: string;
  phone: string;
  acceptTerms: boolean;
};

const initialForm: FormState = {
  name: "",
  email: "",
  company: "",
  taxId: "",
  phone: "",
  acceptTerms: false,
};

export default function CheckoutDialog({
  planId,
  open,
  onOpenChange,
}: CheckoutDialogProps) {
  const plan = PLANS.find((p) => p.id === planId) ?? null;
  const [mode, setMode] = useState<Mode>("pay");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormState>(initialForm);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const reset = () => {
    setForm(initialForm);
    setMode("pay");
    setLoading(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const validate = () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nombre y email son obligatorios.");
      return false;
    }
    if (!form.acceptTerms) {
      toast.error("Debes aceptar los términos y la política de privacidad.");
      return false;
    }
    return true;
  };

  const handleReserve = async () => {
    if (!plan || !validate()) return;
    setLoading(true);

    const lines = [
      `Reserva ${LEGAL.brand}`,
      `Plan: ${plan.name} (${plan.priceLabel}${plan.period})`,
      `Nombre: ${form.name}`,
      `Email: ${form.email}`,
      `Empresa: ${form.company || "-"}`,
      `CIF/NIF: ${form.taxId || "-"}`,
      `Teléfono: ${form.phone || "-"}`,
    ];

    const message = encodeURIComponent(lines.join("\n"));
    window.open(
      `https://wa.me/${LEGAL.phoneE164}?text=${message}`,
      "_blank",
      "noopener,noreferrer"
    );

    toast.success("Reserva preparada. Completa el envío por WhatsApp.");
    setLoading(false);
    handleOpenChange(false);
  };

  const handlePay = async () => {
    if (!plan || !validate()) return;
    setLoading(true);

    try {
      sessionStorage.setItem(
        "m4b_checkout_draft",
        JSON.stringify({
          plan_id: plan.id,
          ...form,
          created_at: new Date().toISOString(),
        })
      );

      const response = await fetch("/api/v1/core/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: plan.id,
          success_url: `${window.location.origin}/pago/exito`,
          cancel_url: `${window.location.origin}/pago/cancelado`,
          customer: {
            name: form.name,
            email: form.email,
            company: form.company,
            tax_id: form.taxId,
            phone: form.phone,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail ||
            "La pasarela de pago aún no está activa. Usa «Reservar» o contacta con nosotros."
        );
      }

      const data = await response.json();
      const checkoutUrl = data.checkout_url || data.redirect_url;
      if (!checkoutUrl) {
        throw new Error("No se recibió URL de pago.");
      }
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo iniciar el pago. Puedes reservar el plan sin pagar ahora."
      );
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {plan ? `Plan ${plan.name}` : "Contratar"}
          </DialogTitle>
          <DialogDescription>
            {plan
              ? `${plan.priceLabel}${plan.period} · ${plan.vatNote}. Factura a nombre de ${LEGAL.companyName}.`
              : "Selecciona un plan"}
          </DialogDescription>
        </DialogHeader>

        {plan && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-muted">
              <button
                type="button"
                onClick={() => setMode("pay")}
                className={`rounded-md py-2 text-sm font-medium transition-colors ${
                  mode === "pay"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Pagar ahora
              </button>
              <button
                type="button"
                onClick={() => setMode("reserve")}
                className={`rounded-md py-2 text-sm font-medium transition-colors ${
                  mode === "reserve"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Reservar
              </button>
            </div>

            <div className="grid gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="checkout-name">Nombre y apellidos *</Label>
                <Input
                  id="checkout-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  autoComplete="name"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="checkout-email">Email *</Label>
                <Input
                  id="checkout-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="checkout-company">Empresa</Label>
                  <Input
                    id="checkout-company"
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    autoComplete="organization"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="checkout-tax">CIF / NIF</Label>
                  <Input
                    id="checkout-tax"
                    value={form.taxId}
                    onChange={(e) => update("taxId", e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="checkout-phone">Teléfono</Label>
                <Input
                  id="checkout-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  autoComplete="tel"
                />
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.acceptTerms}
                onChange={(e) => update("acceptTerms", e.target.checked)}
              />
              <span>
                He leído y acepto los{" "}
                <Link
                  href="/terminos"
                  target="_blank"
                  className="text-primary underline underline-offset-2"
                >
                  términos y condiciones
                </Link>{" "}
                y la{" "}
                <Link
                  href="/privacidad"
                  target="_blank"
                  className="text-primary underline underline-offset-2"
                >
                  política de privacidad
                </Link>
                .
              </span>
            </label>

            {mode === "pay" ? (
              <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Lock className="w-4 h-4 text-primary" />
                  Pago seguro con TPV Banco Sabadell
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Serás redirigido a la pasarela bancaria (Redsys).{" "}
                  {LEGAL.companyName} no almacena datos de tu tarjeta.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  Reserva sin compromiso de pago inmediato
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Te contactaremos para confirmar disponibilidad y abrir el
                  cobro cuando estés listo. Plazo orientativo de validez: 7
                  días.
                </p>
              </div>
            )}

            <Button
              className="w-full"
              size="lg"
              disabled={loading}
              onClick={mode === "pay" ? handlePay : handleReserve}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Procesando...
                </>
              ) : mode === "pay" ? (
                `Pagar ${plan.priceLabel}${plan.period}`
              ) : (
                "Reservar este plan"
              )}
            </Button>

            <p className="text-[11px] text-center text-muted-foreground">
              Prestador: {LEGAL.companyName} · CIF {LEGAL.cif} · {LEGAL.email}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
